import {v4 as uuidv4} from 'uuid';

import {queryCollection, setDocument} from '@/services/FirestoreService';

import {
  BaseScenario,
  BuildingScenario,
  DerivedScenarioDto,
  OverrideEntry,
  PortfolioScenario,
  RecordRef,
  Scenario,
  ScenarioDto,
  UNDERWRITING_SCENARIOS_COLLECTION,
} from '@/lib/underwriting/core/scenario/types';
import type {UserAction} from '@/types/commonTypes';

export function isBuildingScenario(scenario: Scenario): scenario is BuildingScenario {
  return scenario.recordRef.kind === 'building';
}

export function isPortfolioScenario(scenario: Scenario): scenario is PortfolioScenario {
  return scenario.recordRef.kind === 'portfolio';
}

/**
 * Flattens a scenario's overrides into a single map keyed by variable name.
 * Portfolio scenarios merge top-level overrides with each building's nested overrides.
 */
export function collectAllOverrides(scenario: Scenario): Record<string, OverrideEntry> {
  const merged: Record<string, OverrideEntry> = {...(scenario.overrides ?? {})};
  if (isPortfolioScenario(scenario)) {
    for (const snapshot of Object.values(scenario.buildings)) {
      Object.assign(merged, snapshot.overrides ?? {});
    }
  }
  return merged;
}

// Composite doc-ID convention for scenarios. Base uses the 'base' suffix; derived scenarios use a uuid suffix.
export const scenarioDocId = (
  kind: RecordRef['kind'],
  recordId: string,
  suffix: 'base' | string,
): string => `${kind}_${recordId}_${suffix}`;

/**
 * Writes a scenario DTO (base or derived) to Firestore and returns the generated document ID.
 */
export const writeScenario = async (dto: ScenarioDto): Promise<string> => {
  const suffix = dto.isBase ? 'base' : uuidv4();
  const docId = scenarioDocId(dto.recordRef.kind, dto.recordRef.id, suffix);
  await setDocument(UNDERWRITING_SCENARIOS_COLLECTION, docId, dto);
  return docId;
};

/**
 * Builds a blank derived scenario DTO seeded from the base. Empty overrides and capExRows; for portfolios, one empty
 * snapshot per building keyed by base's building ids.
 */
export function buildBlankDerivedDto(
  base: BaseScenario,
  scenarioName: string,
  whoAndNow: UserAction,
): DerivedScenarioDto {
  if (isBuildingScenario(base)) {
    return {
      recordRef: {kind: 'building', id: base.recordRef.id, dataType: 'firestore'},
      scenarioName,
      isBase: false,
      overrides: {},
      capExRows: {rows: [], deletedRowIds: []},
      lastVersionedOverrides: null,
      deleted: null,
      createdOn: whoAndNow,
      editedOn: whoAndNow,
    };
  }
  return {
    recordRef: {kind: 'portfolio', id: base.recordRef.id, dataType: 'firestore'},
    scenarioName,
    isBase: false,
    overrides: {},
    buildings: Object.fromEntries(
      Object.keys(base.buildings).map((id) => [id, {
        capExRows: {rows: [], deletedRowIds: []},
        overrides: {},
      }]),
    ),
    lastVersionedOverrides: null,
    deleted: null,
    createdOn: whoAndNow,
    editedOn: whoAndNow,
  };
}

/**
 * Builds a derived scenario DTO that clones the source's overrides/capExRows. Per-building snapshots are shallow-copied
 */
export function buildDuplicateDerivedDto(
  source: Scenario,
  scenarioName: string,
  whoAndNow: UserAction,
): DerivedScenarioDto {
  if (isBuildingScenario(source)) {
    return {
      recordRef: {kind: 'building', id: source.recordRef.id, dataType: 'firestore'},
      scenarioName,
      isBase: false,
      overrides: {...source.overrides},
      capExRows: {
        rows: [...source.capExRows.rows],
        deletedRowIds: [...source.capExRows.deletedRowIds],
      },
      lastVersionedOverrides: null,
      deleted: null,
      createdOn: whoAndNow,
      editedOn: whoAndNow,
    };
  }
  return {
    recordRef: {kind: 'portfolio', id: source.recordRef.id, dataType: 'firestore'},
    scenarioName,
    isBase: false,
    overrides: {...source.overrides},
    buildings: Object.fromEntries(
      Object.entries(source.buildings).map(([bid, snap]) => [bid, {
        capExRows: {
          rows: [...snap.capExRows.rows],
          deletedRowIds: [...snap.capExRows.deletedRowIds],
        },
        overrides: {...snap.overrides},
        // Copy only explicit toggles — an absent flag stays absent so the copy keeps inheriting from base.
        ...(snap.excluded !== undefined ? {excluded: snap.excluded} : {}),
        ...(snap.specSnapshot !== undefined ? {specSnapshot: snap.specSnapshot} : {}),
      }]),
    ),
    lastVersionedOverrides: null,
    deleted: null,
    createdOn: whoAndNow,
    editedOn: whoAndNow,
  };
}

export const queryUnderwritingScenarios = async (
  kind: RecordRef['kind'],
  id: string,
  {includeDeleted = false}: {includeDeleted?: boolean} = {},
): Promise<Scenario[]> => {
  const docs = await queryCollection(UNDERWRITING_SCENARIOS_COLLECTION, {
    where: [
      {field: 'recordRef.id', value: id, opStr: '=='},
      {field: 'recordRef.kind', value: kind, opStr: '=='},
    ],
  }, true) as Scenario[];
  if (includeDeleted) return docs;
  return docs.filter((s) => s.deleted === null);
};
